/**
 * Showcase API client.
 *
 * Users opt-in to feature their published videos on the public landing page.
 * Admin approves/rejects submissions. Approved entries feed the "Wall of
 * Outputs" on landing variant B and the showcase sections across all variants.
 */
import { apiGet, apiPost, apiPatch } from "./client.ts";

export interface ShowcaseEntry {
  id: string;
  videoId: string;
  userId: string;
  title: string;
  channelName: string;
  niche: string;
  thumbnailUrl: string | null;
  videoUrl: string | null;
  duration: string;
  viewCount: number | null;
  status: "pending" | "approved" | "rejected";
  createdAt: string;
  approvedAt: string | null;
}

export const showcaseApi = {
  /** User submits their published video for showcase consideration. */
  submit: (videoId: string) =>
    apiPost<{ ok: boolean; id: string }>("/showcase/submit", { videoId }),

  /** Public: get approved showcase entries (no auth required). */
  list: (params: { limit?: number } = {}) => {
    const qs = params.limit ? `?limit=${params.limit}` : "";
    return apiGet<{ entries: ShowcaseEntry[] }>(`/showcase${qs}`);
  },

  /** Admin: list all submissions (pending, approved, rejected). */
  adminList: (params: { status?: string; limit?: number; offset?: number } = {}) => {
    const qs = new URLSearchParams();
    if (params.status) qs.set("status", params.status);
    if (params.limit) qs.set("limit", String(params.limit));
    if (params.offset) qs.set("offset", String(params.offset));
    const q = qs.toString();
    return apiGet<{
      entries: ShowcaseEntry[];
      pagination: { limit: number; offset: number; total: number };
    }>(`/admin/showcase${q ? `?${q}` : ""}`);
  },

  /** Admin: approve or reject a submission. */
  moderate: (id: string, action: "approve" | "reject") =>
    apiPatch<{ ok: boolean }>(`/admin/showcase/${id}`, { action }),
};
